import { useGetB2bOverview, useGetB2bWorkflow } from '@np-forge/api-client/b2b'
import type { B2bOverviewWorkspaceResponse } from '@np-forge/api-client/types'
import { Bug } from 'lucide-react'
import { useState, type FC } from 'react'
import { useMatch, useNavigate } from 'react-router-dom'

import { MembersIcon, SettingsIcon, WorkflowsIcon } from '@/components/core/Icon/icons'
import { B2B_PORTAL_NAME } from '@/constants/b2bPortal'
import { SIDEBAR_TEST_IDS } from '@/constants/testIds'
import { B2B_STAFF_ANY_PATH, ROUTES } from '@/routes/paths'

import { B2bWorkspaceDropdown } from './B2bWorkspaceDropdown'
import { SidebarFrame } from './SidebarFrame'
import { SidebarNavLink } from './SidebarNavLink'

interface B2bSidebarProps {
  userEmail: string
  isOpen: boolean
  onToggle?: () => void
  onLogout: () => void
  /** "Debug Console", the staff console, only when /v1/users/me says `can_use_staff_console`. */
  showsStaffEntry: boolean
}

/**
 * The B2B portal's sidebar, in the shared frame. Its workspaces and what the person may do in each come from
 * `GET /v1/b2b/me`; it never reads the original workspaces, which would create one for a person who has none.
 */
export const B2bSidebar: FC<B2bSidebarProps> = ({ userEmail, isOpen, onToggle, onLogout, showsStaffEntry }) => {
  const navigate = useNavigate()
  const { data: overview } = useGetB2bOverview()
  const workspaces = overview?.workspaces ?? []
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(null)

  const isHomeActive = !!useMatch(ROUTES.B2B.HOME.fullPath)
  const workflowsMatch = useMatch(ROUTES.B2B.WORKSPACE_WORKFLOWS.fullPath)
  const workflowPageMatch = useMatch(ROUTES.B2B.WORKFLOW.fullPath)
  const workflowSessionMatch = useMatch(ROUTES.B2B.WORKFLOW_SESSION.fullPath)
  const workflowMatch = workflowPageMatch ?? workflowSessionMatch
  const membersMatch = useMatch(ROUTES.B2B.WORKSPACE_MEMBERS.fullPath)
  const setupMatch = useMatch(ROUTES.B2B.WORKSPACE_SETUP.fullPath)
  const isStaffActive = !!useMatch(B2B_STAFF_ANY_PATH)

  // On a workspace's own page, or one of its workflows (the page's own read, shared through the cache), that workspace
  // is the current one; one the person is not in is shown as none rather than as another.
  const routeWorkflowId = workflowMatch?.params.workflowId
  const { data: routeWorkflow } = useGetB2bWorkflow(routeWorkflowId ?? '', {
    query: { enabled: typeof routeWorkflowId !== 'undefined' },
  })
  const routeWorkspaceId =
    (workflowsMatch ?? membersMatch ?? setupMatch)?.params.workspaceId ??
    (typeof routeWorkflowId !== 'undefined' ? routeWorkflow?.workspace_id : undefined)
  const currentWorkspace =
    typeof routeWorkspaceId !== 'undefined'
      ? workspaces.find(workspace => workspace.id === routeWorkspaceId)
      : (workspaces.find(workspace => workspace.id === selectedWorkspaceId) ?? workspaces[0])
  const capabilities = currentWorkspace?.my_workspace_capabilities

  // The same page of the workspace picked where the person may open it, else its workflows.
  const pageInWorkspace = (picked: B2bOverviewWorkspaceResponse) => {
    const workspaceId = picked.id
    if (membersMatch !== null && picked.my_workspace_capabilities.can_manage_members) {
      return ROUTES.B2B.WORKSPACE_MEMBERS.buildPath({ workspaceId })
    }
    if (setupMatch !== null && picked.my_workspace_capabilities.can_manage_settings) {
      return ROUTES.B2B.WORKSPACE_SETUP.buildPath({ workspaceId })
    }
    return ROUTES.B2B.WORKSPACE_WORKFLOWS.buildPath({ workspaceId })
  }

  const selectWorkspace = (workspaceId: string) => {
    setSelectedWorkspaceId(workspaceId)
    const picked = workspaces.find(workspace => workspace.id === workspaceId)
    if (typeof picked !== 'undefined') void navigate(pageInWorkspace(picked))
  }

  return (
    <SidebarFrame
      isOpen={isOpen}
      onToggle={onToggle}
      // The portal's name while the overview loads, and for someone in no B2B workspace.
      workspaceName={currentWorkspace?.name ?? B2B_PORTAL_NAME}
      renderWorkspaceDropdown={closeDropdown => (
        <B2bWorkspaceDropdown
          workspaces={workspaces}
          currentWorkspaceId={currentWorkspace?.id}
          onSelect={workspaceId => {
            selectWorkspace(workspaceId)
            closeDropdown()
          }}
        />
      )}
      nav={
        <SidebarNavLink
          to={
            typeof currentWorkspace === 'undefined'
              ? ROUTES.B2B.HOME.fullPath
              : ROUTES.B2B.WORKSPACE_WORKFLOWS.buildPath({ workspaceId: currentWorkspace.id })
          }
          label="Workflows"
          icon={<WorkflowsIcon />}
          isActive={isHomeActive || workflowsMatch !== null || workflowMatch !== null}
          isSidebarOpen={isOpen}
          placement="nav"
          testId={SIDEBAR_TEST_IDS.NAV_B2B_WORKFLOWS}
        />
      }
      bottomItems={
        <>
          {showsStaffEntry && (
            <SidebarNavLink
              to={ROUTES.B2B.STAFF.fullPath}
              label="Debug Console"
              icon={<Bug className="sidebar__icon" aria-hidden="true" />}
              isActive={isStaffActive}
              isSidebarOpen={isOpen}
              placement="bottom"
              testId={SIDEBAR_TEST_IDS.NAV_B2B_STAFF}
            />
          )}
          {currentWorkspace && capabilities?.can_manage_members && (
            <SidebarNavLink
              to={ROUTES.B2B.WORKSPACE_MEMBERS.buildPath({ workspaceId: currentWorkspace.id })}
              label="Workspace members"
              icon={<MembersIcon />}
              isActive={membersMatch !== null}
              isSidebarOpen={isOpen}
              placement="bottom"
              testId={SIDEBAR_TEST_IDS.NAV_WORKSPACE_MEMBERS}
            />
          )}
          {currentWorkspace && capabilities?.can_manage_settings && (
            <SidebarNavLink
              to={ROUTES.B2B.WORKSPACE_SETUP.buildPath({ workspaceId: currentWorkspace.id })}
              label="Workspace setup"
              icon={<SettingsIcon />}
              isActive={setupMatch !== null}
              isSidebarOpen={isOpen}
              placement="bottom"
              testId={SIDEBAR_TEST_IDS.NAV_WORKSPACE_SETTINGS}
            />
          )}
        </>
      }
      userEmail={userEmail}
      onLogout={onLogout}
      onMyAccount={() => void navigate(ROUTES.B2B.ACCOUNT.fullPath)}
      termsOfServicePath={ROUTES.B2B.TERMS_OF_SERVICE.fullPath}
    />
  )
}
